import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { formatDate, useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaCalendarCheck } from "react-icons/fa";
import { deleteReservation, fetchTentativeReservation } from "redux/features/reservations/reservationSlice";
import Input from "shared/Input/Input";

export interface TentativeReservationsProps {
  className?: string;
}

interface Reservation {
  id: number;
  reference: string;
  createdAt: string;
  Location?: { title: string };
  Client?: { name: string; lastName: string };
  Owner?: { name: string; lastName: string; companyName?: string };
  price: number;
  selectedDate: string;
  startHour: string;
  endHour: string;
}

interface ModalState {
  isDeleteModalOpen: boolean;
  isViewModalOpen: boolean;
  selectedItem: Reservation | null;
  loading: boolean;
}

const TentativeReservations: FC<TentativeReservationsProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const { reservations, totalCount, totalPages, currentPage, loading } = useAppSelector((state) => ({
    reservations: state.featureReservation?.tentative || [],
    totalCount: state.featureReservation?.count || 0,
    totalPages: state.featureReservation?.totalPages || 0,
    currentPage: state.featureReservation?.currentPage || 1,
    loading: state.featureReservation?.loading || "idle",
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isDeleteModalOpen: false,
    isViewModalOpen: false,
    selectedItem: null,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(fetchTentativeReservation({ page, reference: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleDeleteModalOpen = (reservation: Reservation) => {
    setModalState({ ...modalState, selectedItem: reservation, isDeleteModalOpen: true });
  };

  const handleViewModalOpen = (reservation: Reservation) => {
    setModalState({ ...modalState, selectedItem: reservation, isViewModalOpen: true });
  };

  const handleModalClose = () => {
    setModalState({
      ...modalState,
      isDeleteModalOpen: false,
      isViewModalOpen: false,
      selectedItem: null,
    });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      setModalState((prev) => ({ ...prev, loading: true }));
      await dispatch(deleteReservation(modalState.selectedItem.id));
      setModalState({
        ...modalState,
        isDeleteModalOpen: false,
        loading: false,
        selectedItem: null,
      });
      await dispatch(fetchTentativeReservation({ page, reference: debouncedSearchTerm }));
    }
  };

  const handleTableAction = (action: string, item: Reservation) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "view") {
      handleViewModalOpen(item);
    }
  };

  const columns = [
    {
      key: "date",
      label: "Date",
      render: (item: Reservation) => <span className="align-middle">{formatDate(item.createdAt)}</span>,
    },
    {
      key: "reference",
      label: "Référence",
      render: (item: Reservation) => <span className="align-middle">{item.reference}</span>,
    },
    {
      key: "espace",
      label: "Espace",
      render: (item: Reservation) => <span className="align-middle">{item.Location?.title || "N/A"}</span>,
    },
    {
      key: "client",
      label: "Client",
      render: (item: Reservation) => (
        <span className="align-middle">{item.Client ? `${item.Client.name} ${item.Client.lastName}` : "N/A"}</span>
      ),
    },
    {
      key: "proprietaire",
      label: "Propriétaire",
      render: (item: Reservation) => (
        <span className="align-middle">
          {item.Owner ? `${item.Owner.name} ${item.Owner.lastName || item.Owner.companyName || "N/A"}` : "N/A"}
        </span>
      ),
    },
    {
      key: "montant",
      label: "Montant",
      render: (item: Reservation) => <span className="align-middle">{item.price} €</span>,
    },
    {
      key: "journee",
      label: "Journée",
      render: (item: Reservation) => <span className="align-middle">{formatDate(item.selectedDate)}</span>,
    },
    {
      key: "duree",
      label: "Durée",
      render: (item: Reservation) => <span className="align-middle">{`${item.startHour} - ${item.endHour}`}</span>,
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaCalendarCheck className="text-3xl text-yellow-500 dark:text-yellow-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste des Tentatives de Réservation
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par référence..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search reservations"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {loading !== "pending" && (
            <>
              {modalState.isDeleteModalOpen && (
                <DeleteModal
                  open={modalState.isDeleteModalOpen}
                  handleClose={handleModalClose}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: modalState.selectedItem?.id || 0,
                    designation: modalState.selectedItem?.reference || "",
                  }}
                />
              )}

              {/* {modalState.isViewModalOpen && (
                <ReservationDetailsPage
                  open={modalState.isViewModalOpen}
                  handleClose={handleModalClose}
                  data={modalState.selectedItem}
                />
              )} */}
              <DynamicTable
                columns={columns}
                data={reservations}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["delete"]}
              />
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default TentativeReservations;
